import "leaflet/dist/leaflet.css";
import "./EmergencyMapReadOnly.css";

import { type BubbleMapLocationDto, type DayOfEmergencyParticipantDto } from "@crowdcoursing/api/openapi";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { Alert, Box, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GeoJSON, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";

import { STATUS_OPTIONS, statusLabelForKey } from "~ui/pages/VisitorManagement/emergencyCommandCenterShared";
import { positionForBubbleMapLocation } from "~utils/emergencyMapLocationGeometry";

const DefaultIcon = L.icon({
  iconAnchor: [12, 41],
  iconRetinaUrl: markerIcon2x,
  iconSize: [25, 41],
  iconUrl: markerIcon,
  popupAnchor: [1, -34],
  shadowAnchor: [12, 41],
  shadowSize: [41, 41],
  shadowUrl: markerShadow,
});
L.Marker.prototype.options.icon = DefaultIcon;

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;

/** API may include polygon GeoJSON before OpenAPI regen lists it. */
export type EmergencyMapLocationRow = BubbleMapLocationDto & { polygonGeoJson?: string | null };

const STATUS_SORT_ORDER = new Map(STATUS_OPTIONS.map((o, i) => [o.value, i]));

function sortStatusKeysForStacks(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const ia = STATUS_SORT_ORDER.get(a) ?? 999;
    const ib = STATUS_SORT_ORDER.get(b) ?? 999;
    if (ia !== ib) {
      return ia - ib;
    }
    return a.localeCompare(b);
  });
}

/** Spread multiple status stacks around one base point */
function offsetForStack(base: [number, number], index: number, count: number): [number, number] {
  if (count <= 1) {
    return base;
  }
  const angle = (2 * Math.PI * index) / count - Math.PI / 2;
  const r = 0.00014;
  return [base[0] + r * Math.cos(angle), base[1] + r * Math.sin(angle)];
}

function normalizeStatusKey(statusKey: string | null | undefined): string {
  const s = statusKey?.trim();
  return s && s.length > 0 ? s : "not-reported";
}

const STACK_ICON_SIZE = 36;

function countedStatusDivIcon(count: number, fillColor: string): L.DivIcon {
  const s = STACK_ICON_SIZE;
  const inner = `<div style="display:flex;align-items:center;justify-content:center;width:${s}px;height:${s}px;border-radius:50%;background:${fillColor};color:#fff;font-weight:700;font-size:15px;line-height:1;border:2px solid #1b1b1b;box-shadow:0 2px 6px rgba(0,0,0,0.35);font-family:system-ui,sans-serif">${count}</div>`;
  return L.divIcon({
    className: "emergency-map-stack-pin",
    html: inner,
    iconAnchor: [s / 2, s / 2],
    iconSize: [s, s],
    popupAnchor: [0, -s / 2],
  });
}

function statusMarkerColor(statusKey: string | null | undefined): string {
  switch (statusKey) {
    case "safe":
      return "#2e7d32";
    case "needs-assistance":
      return "#c62828";
    case "hiding":
    case "sheltering-in-place":
      return "#ef6c00";
    case "evacuating":
      return "#6a1b9a";
    case "absent-offsite":
      return "#1565c0";
    case "not-reported":
      return "#757575";
    default:
      return "#1976d2";
  }
}

function participantLabel(p: DayOfEmergencyParticipantDto): string {
  const first = p.firstName?.trim() ?? "";
  const last = p.lastName?.trim() ?? "";
  const combined = `${first} ${last}`.trim();
  return combined || "Participant";
}

function ParticipantRosterList({ people }: { people: DayOfEmergencyParticipantDto[] }) {
  if (people.length === 0) {
    return null;
  }
  const sorted = [...people].sort((a, b) => participantLabel(a).localeCompare(participantLabel(b)));
  return (
    <Stack component="ul" spacing={0.75} sx={{ listStyle: "none", m: 0, maxHeight: 220, overflow: "auto", p: 0 }}>
      {sorted.map((p) => (
        <Typography component="li" key={p.userId ?? participantLabel(p)} variant="body2">
          <strong>{participantLabel(p)}</strong> — {statusLabelForKey(p.statusKey)}
        </Typography>
      ))}
    </Stack>
  );
}

/** Section for polygon/point popups: header + list */
function ParticipantRosterPopup({ people }: { people: DayOfEmergencyParticipantDto[] }) {
  if (people.length === 0) {
    return null;
  }
  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Typography color="text.secondary" sx={{ mb: 0.5 }} variant="caption">
        People at this location ({people.length})
      </Typography>
      <ParticipantRosterList people={people} />
    </>
  );
}

function collectBoundsPositions(locations: EmergencyMapLocationRow[]): [number, number][] {
  const pts: [number, number][] = [];
  for (const loc of locations) {
    const poly = loc.polygonGeoJson?.trim();
    if (poly) {
      try {
        const g = JSON.parse(poly) as { coordinates?: number[][][] };
        const ring = g.coordinates?.[0];
        if (ring?.length) {
          for (const c of ring) {
            if (c.length >= 2) {
              pts.push([c[1], c[0]]);
            }
          }
        }
      } catch {
        /* ignore */
      }
    } else if (typeof loc.latitude === "number" && typeof loc.longitude === "number") {
      pts.push([loc.latitude, loc.longitude]);
    }
  }
  return pts;
}

function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) {
      return;
    }
    const b = L.latLngBounds(positions);
    map.fitBounds(b, { maxZoom: 18, padding: [40, 40] });
  }, [map, positions]);
  return null;
}

function MapInstanceBridge({ onReady }: { onReady: (map: L.Map | null) => void }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
    return () => onReady(null);
  }, [map, onReady]);
  return null;
}

export interface EmergencyMapReadOnlyProps {
  /** Bubble id for `data-bubble-id` (optional). */
  bubbleId?: string;
  error?: boolean;
  /** Map stroke / polygon color (e.g. theme primary). */
  highlightColor: string;
  loading?: boolean;
  locations: EmergencyMapLocationRow[];
  /**
   * When set, draws stacked pins at each reported `bubbleMapLocationId` (joined to `locations`) and at exact GPS
   * (`reportedLatitude` / `reportedLongitude`) when no named location is set. Status-only with no location or GPS does
   * not produce a pin.
   */
  participants?: DayOfEmergencyParticipantDto[];
  /** School / site name for caption copy. */
  siteName?: string;
}

/**
 * Read-only Leaflet map for emergency map locations (points and GeoJSON polygons).
 * Used by law enforcement and similar command-center views that load locations via token-based APIs.
 */
export function EmergencyMapReadOnly({
  bubbleId,
  error = false,
  highlightColor,
  loading = false,
  locations,
  participants,
  siteName,
}: EmergencyMapReadOnlyProps) {
  const sorted = useMemo(
    () => [...locations].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [locations],
  );
  const participantsByLocationId = useMemo(() => {
    const m = new Map<string, DayOfEmergencyParticipantDto[]>();
    if (!participants?.length) {
      return m;
    }
    for (const p of participants) {
      const lid = p.bubbleMapLocationId?.trim();
      if (!lid) {
        continue;
      }
      let row = m.get(lid);
      if (!row) {
        row = [];
        m.set(lid, row);
      }
      row.push(p);
    }
    return m;
  }, [participants]);

  /** Participants grouped by rounded GPS when they have no `bubbleMapLocationId` */
  const participantsByGpsKey = useMemo(() => {
    const m = new Map<string, DayOfEmergencyParticipantDto[]>();
    if (!participants?.length) {
      return m;
    }
    for (const p of participants) {
      if (p.bubbleMapLocationId?.trim()) {
        continue;
      }
      const lat = p.reportedLatitude;
      const lng = p.reportedLongitude;
      if (typeof lat !== "number" || typeof lng !== "number") {
        continue;
      }
      const key = `gps:${lat.toFixed(6)}:${lng.toFixed(6)}`;
      let row = m.get(key);
      if (!row) {
        row = [];
        m.set(key, row);
      }
      row.push(p);
    }
    return m;
  }, [participants]);

  /** One numbered pin per (location × status); popup lists everyone at that location */
  const stackedPins = useMemo(() => {
    const pins: {
      allAtLocation: DayOfEmergencyParticipantDto[];
      count: number;
      isGps: boolean;
      key: string;
      position: [number, number];
      statusKey: string;
    }[] = [];
    for (const [locId, allAtLocation] of participantsByLocationId) {
      const loc = sorted.find((l) => String(l.id) === locId);
      if (!loc) {
        continue;
      }
      const base = positionForBubbleMapLocation(loc);
      if (!base) {
        continue;
      }
      const byStatus = new Map<string, DayOfEmergencyParticipantDto[]>();
      for (const p of allAtLocation) {
        const sk = normalizeStatusKey(p.statusKey);
        let bucket = byStatus.get(sk);
        if (!bucket) {
          bucket = [];
          byStatus.set(sk, bucket);
        }
        bucket.push(p);
      }
      const statusKeys = sortStatusKeysForStacks([...byStatus.keys()]);
      const nStacks = statusKeys.length;
      statusKeys.forEach((sk, index) => {
        const group = byStatus.get(sk);
        if (!group) {
          return;
        }
        pins.push({
          allAtLocation,
          count: group.length,
          isGps: false,
          key: `${locId}::${sk}`,
          position: offsetForStack(base, index, nStacks),
          statusKey: sk,
        });
      });
    }
    for (const [gpsKey, allAtLocation] of participantsByGpsKey) {
      const first = allAtLocation[0];
      const lat = first?.reportedLatitude;
      const lng = first?.reportedLongitude;
      if (typeof lat !== "number" || typeof lng !== "number") {
        continue;
      }
      const base: [number, number] = [lat, lng];
      const byStatus = new Map<string, DayOfEmergencyParticipantDto[]>();
      for (const p of allAtLocation) {
        const sk = normalizeStatusKey(p.statusKey);
        let bucket = byStatus.get(sk);
        if (!bucket) {
          bucket = [];
          byStatus.set(sk, bucket);
        }
        bucket.push(p);
      }
      const statusKeys = sortStatusKeysForStacks([...byStatus.keys()]);
      const nStacks = statusKeys.length;
      statusKeys.forEach((sk, index) => {
        const group = byStatus.get(sk);
        if (!group) {
          return;
        }
        pins.push({
          allAtLocation,
          count: group.length,
          isGps: true,
          key: `${gpsKey}::${sk}`,
          position: offsetForStack(base, index, nStacks),
          statusKey: sk,
        });
      });
    }
    return pins;
  }, [participantsByGpsKey, participantsByLocationId, sorted]);
  const boundsPositions = useMemo(() => {
    const fromLocs = collectBoundsPositions(sorted);
    const gpsPts: [number, number][] = [];
    if (participants?.length) {
      for (const p of participants) {
        if (p.bubbleMapLocationId?.trim()) {
          continue;
        }
        if (typeof p.reportedLatitude === "number" && typeof p.reportedLongitude === "number") {
          gpsPts.push([p.reportedLatitude, p.reportedLongitude]);
        }
      }
    }
    return [...fromLocs, ...gpsPts];
  }, [participants, sorted]);
  const mapCenter = boundsPositions.length > 0 ? boundsPositions[0] : DEFAULT_CENTER;
  const mapZoom = boundsPositions.length > 0 ? 16 : DEFAULT_ZOOM;

  const [leafletMap, setLeafletMap] = useState<L.Map | null>(null);
  const [locatingMe, setLocatingMe] = useState(false);
  const [geoHint, setGeoHint] = useState<string | null>(null);

  const handleGoToMyLocation = useCallback(() => {
    if (!leafletMap) {
      return;
    }
    if (!navigator.geolocation) {
      setGeoHint("Location is not supported in this browser.");
      return;
    }
    setGeoHint(null);
    setLocatingMe(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        leafletMap.setView([lat, lng], Math.max(leafletMap.getZoom(), 16));
        setLocatingMe(false);
      },
      (err) => {
        setGeoHint(err.message || "Could not get your location. Check browser permissions.");
        setLocatingMe(false);
      },
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: 20_000 },
    );
  }, [leafletMap]);

  if (loading) {
    return (
      <Box alignItems="center" display="flex" height={420} justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Could not load map locations.</Alert>;
  }

  return (
    <Box data-bubble-id={bubbleId || undefined}>
      <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
        Configured locations for this emergency{siteName ? ` (${siteName})` : ""}. Same data school staff set under
        Emergency Management. Numbered pins stack people who share the same status at a location or GPS point; click a
        pin or the area to see everyone there. Status alone (no map location or GPS) does not add a pin.
      </Typography>
      {sorted.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          No map locations have been published for this site yet.
        </Alert>
      ) : null}
      <Box sx={{ borderRadius: 1, overflow: "hidden", position: "relative" }}>
        <MapContainer
          center={mapCenter}
          scrollWheelZoom
          style={{ borderRadius: 8, height: 420, width: "100%", zIndex: 0 }}
          zoom={mapZoom}
        >
          <MapInstanceBridge onReady={setLeafletMap} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {boundsPositions.length > 0 ? <FitBounds positions={boundsPositions} /> : null}
          {sorted.map((loc) => {
            if (!loc.id) {
              return null;
            }
            const peopleHere = participantsByLocationId.get(String(loc.id)) ?? [];
            const poly = loc.polygonGeoJson?.trim();
            if (poly) {
              try {
                const parsed = JSON.parse(poly) as { coordinates?: number[][][]; type?: string };
                if (parsed.type !== "Polygon" || !parsed.coordinates) {
                  return null;
                }
                const geom = { coordinates: parsed.coordinates, type: "Polygon" as const };
                return (
                  <GeoJSON
                    data={geom}
                    key={loc.id}
                    pathOptions={{ color: highlightColor, fillOpacity: 0.25, weight: 2 }}
                  >
                    <Popup>
                      <Typography fontWeight={600} variant="subtitle2">
                        {loc.label}
                      </Typography>
                      {loc.description ? <Typography variant="body2">{loc.description}</Typography> : null}
                      <ParticipantRosterPopup people={peopleHere} />
                    </Popup>
                  </GeoJSON>
                );
              } catch {
                return null;
              }
            }
            if (typeof loc.latitude === "number" && typeof loc.longitude === "number") {
              return (
                <Marker key={loc.id} position={[loc.latitude, loc.longitude]}>
                  <Popup>
                    <Typography fontWeight={600} variant="subtitle2">
                      {loc.label}
                    </Typography>
                    {loc.description ? <Typography variant="body2">{loc.description}</Typography> : null}
                    <ParticipantRosterPopup people={peopleHere} />
                  </Popup>
                </Marker>
              );
            }
            return null;
          })}
          {stackedPins.map(({ allAtLocation, count, isGps, key, position, statusKey }) => {
            const fill = statusMarkerColor(statusKey);
            return (
              <Marker icon={countedStatusDivIcon(count, fill)} key={key} position={position} zIndexOffset={650}>
                <Popup>
                  <Typography fontWeight={600} variant="subtitle2">
                    {statusLabelForKey(statusKey)} ({count})
                  </Typography>
                  <Typography color="text.secondary" sx={{ display: "block", mb: 0.5 }} variant="caption">
                    {isGps
                      ? `${count === 1 ? "1 person" : `${count} people`} with this status at reported GPS (WGS84). Everyone at this position:`
                      : `${count === 1 ? "1 person" : `${count} people`} with this status. Everyone at this location:`}
                  </Typography>
                  {allAtLocation.length > 0 ? (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <ParticipantRosterList people={allAtLocation} />
                    </>
                  ) : null}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        <Tooltip title="Go to my location">
          <span>
            <IconButton
              aria-label="Go to my location"
              color="primary"
              disabled={!leafletMap || locatingMe}
              onClick={handleGoToMyLocation}
              size="medium"
              sx={{
                "&:disabled": { bgcolor: "action.disabledBackground" },
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                boxShadow: 2,
                position: "absolute",
                right: 12,
                top: 12,
                zIndex: 1000,
              }}
            >
              {locatingMe ? <CircularProgress color="inherit" size={22} /> : <MyLocationIcon sx={{ fontSize: 26 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {geoHint ? (
        <Alert onClose={() => setGeoHint(null)} severity="warning" sx={{ mt: 1 }} variant="outlined">
          {geoHint}
        </Alert>
      ) : null}
    </Box>
  );
}
